import { cn } from "@/lib/utils";

export type SegmentedOption<T extends string> = {
  value: T;
  label: string;
  /** Optional short hint for title/aria. */
  description?: string;
};

/**
 * Explicit multi-option switcher (Light/Dark/Auto pattern) — radiogroup.
 * Prefer this over cycle-click for 2–4 named prefs.
 */
export function SegmentedControl<T extends string>(props: {
  value: T;
  options: readonly SegmentedOption<T>[];
  onChange: (next: T) => void;
  label: string;
  className?: string;
  size?: "sm" | "md";
}) {
  const size = props.size ?? "sm";
  return (
    <div
      role="radiogroup"
      aria-label={props.label}
      className={cn(
        "inline-flex max-w-full flex-wrap rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-background)]/60 p-0.5",
        props.className,
      )}
    >
      {props.options.map((opt) => {
        const selected = props.value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            title={opt.description ?? opt.label}
            className={cn(
              "rounded-[calc(var(--radius-md)-2px)] border-0 font-medium transition-colors",
              size === "sm"
                ? "px-2.5 py-1 text-xs"
                : "px-3 py-1.5 text-sm",
              selected
                ? "bg-[var(--color-card)] text-[var(--color-foreground)] shadow-[var(--shadow-control)]"
                : "bg-transparent text-[var(--color-muted)] hover:text-[var(--color-foreground)]",
            )}
            onClick={() => {
              if (!selected) props.onChange(opt.value);
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
